import * as React from 'react';
import Box from '@mui/material/Box';
import { BookedEventType, CalendarRowSize, Order, Photographer } from '../../BookingsCalendarTypes';
import {
    getBookedEventIndex,
    getBusinessHours,
    getFiveMinutesSlots
} from './timelineCalendarHelperFuncitons';
import ICalendarItem from '../../../../calendar/Types';
import CalendarGrid from './TimelineCalendarGrid';
import TimelineCalendarContent from './TimelineCalendarContent';
import { ContainerSizeAndPosition } from '../../state/useCalendarContainerWidth';
import useUpdateBookingInfo from '../../state/useUpdateBookingInfo';

type TimelineCalendarPhotographerRowProps = {
    containerSizeAndPosition: ContainerSizeAndPosition;
    photographer: Photographer;
    rowIndex: number;
    selectedOrder: Order;
    onChangeBookingInfo: (bookingInfo: Partial<ICalendarItem>) => void;
    bookingInfo: Partial<ICalendarItem>;
    isAdvancedMode: boolean;
    selectedDate: Date;
    onChangePhotographer: (newPhotographer: Photographer) => void;
    hideTravelTimes: boolean;
    activeRow: number;
    onActiveRow: (activeRow: number) => void;
};
const gridRowStyle = (
    totalNoTimeSlots,
    hasNewOrderInCalendar,
    hasNoEventsInCalendar,
    isActiveRow
) => {
    return {
        display: 'grid',
        gridTemplateColumns: `repeat(${totalNoTimeSlots}, 1fr )`,
        maxWidth: '100%',
        position: 'relative',
        alignItems: 'center',
        height:
            hasNewOrderInCalendar || isActiveRow
                ? CalendarRowSize.large
                : hasNoEventsInCalendar
                ? CalendarRowSize.small
                : CalendarRowSize.medium
    };
};

const TimelineCalendarPhotographerRow = React.forwardRef(
    (
        {
            containerSizeAndPosition,
            photographer,
            rowIndex,
            selectedOrder,
            onChangeBookingInfo,
            bookingInfo,
            isAdvancedMode,
            selectedDate,
            onChangePhotographer,
            hideTravelTimes,
            activeRow,
            onActiveRow
        }: TimelineCalendarPhotographerRowProps,
        scrollRef: React.MutableRefObject<HTMLElement>
    ) => {
        let businessHours: Date[] = selectedDate && getBusinessHours(selectedDate);
        let fiveMinuteTimeSlots: Date[] = selectedDate && getFiveMinutesSlots(selectedDate);

        let { movedStartIndex, onDropUpdateBookingInfo, lengthOfBooking } = useUpdateBookingInfo({
            businessHours,
            bookingInfo,
            fiveMinuteTimeSlots,
            onChangeBookingInfo,
            onChangePhotographer
        });

        let totalNoTimeSlots: number = fiveMinuteTimeSlots && fiveMinuteTimeSlots.length;
        let isOnFirstRow: boolean = rowIndex === 0;

        let isFirstPhotographer: boolean =
            bookingInfo && photographer && photographer.id === bookingInfo.userId;

        let bookingLength = lengthOfBooking();

        const [firstCalendarSlotForBooking, setFirstCalendarSlotForBooking] =
            React.useState<number>(getBookedEventIndex(photographer, selectedOrder));
        const [preventResize, setPreventResize] = React.useState<boolean>(false);
        const [isResizing, setIsResizing] = React.useState<boolean>(false);

        React.useEffect(() => {
            setFirstCalendarSlotForBooking(getBookedEventIndex(photographer, selectedOrder));
        }, [selectedDate]);

        const onChangePreventResize = React.useCallback((preventResize: boolean) => {
            setPreventResize(preventResize);
        }, []);

        const onChangeIsResizing = React.useCallback((isResizing: boolean) => {
            setIsResizing(isResizing);
        }, []);

        let hasNoEventsInCalendar: boolean =
            photographer.bookedEvents.filter(
                (event) =>
                    event.type === BookedEventType.se360Booking ||
                    event.type === BookedEventType.scandphotosBooking ||
                    event.type === BookedEventType.blocked
            ).length < 1;
        let hasNewOrderInCalendar: boolean = isFirstPhotographer;

        let isActiveRow = rowIndex === activeRow;

        return (
            <Box
                aria-label={'timeline-calendar-row'}
                key={
                    photographer.id +
                    bookingLength +
                    (hideTravelTimes ? 1 : 0) +
                    (hasNewOrderInCalendar ? 1 : 0)
                }
                sx={gridRowStyle(
                    totalNoTimeSlots,
                    hasNewOrderInCalendar,
                    hasNoEventsInCalendar,
                    isActiveRow
                )}
            >
                <CalendarGrid
                    photographer={photographer}
                    newBookingLenght={bookingLength}
                    rowIndex={rowIndex}
                    onDrop={onDropUpdateBookingInfo}
                    selectedDate={selectedDate}
                    hideTravelTimes={hideTravelTimes}
                    preventResize={preventResize}
                    isResizing={isResizing}
                    hasNewOrderInCalendar={hasNewOrderInCalendar}
                    hasNoEventsInCalendar={hasNoEventsInCalendar}
                    isActiveRow={rowIndex === activeRow}
                    onActiveRow={onActiveRow}
                />
                <TimelineCalendarContent
                    ref={scrollRef}
                    containerSizeAndPosition={containerSizeAndPosition}
                    firstCalendarSlotForBooking={firstCalendarSlotForBooking}
                    photographer={photographer}
                    isOnFirstRow={isOnFirstRow}
                    selectedOrder={isFirstPhotographer && selectedOrder ? selectedOrder : null}
                    bookingInfo={bookingInfo}
                    movedBookingIndex={movedStartIndex}
                    newBookingLenght={bookingLength}
                    isAdvancedMode={isAdvancedMode}
                    selectedDate={selectedDate}
                    onPreventResize={onChangePreventResize}
                    onResizing={onChangeIsResizing}
                    hasNoEventsInCalendar={hasNoEventsInCalendar}
                    isActiveRow={rowIndex === activeRow}
                />
            </Box>
        );
    }
);

export default TimelineCalendarPhotographerRow;
